import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { TaskWithSubtasks } from '@task-tracker/shared';
import { apiClient } from '../lib/api-client';

export type TaskHierarchyTarget = {
  projectId?: string;
  taskId: string;
  parentTaskId?: string | null;
};

export type TaskHierarchyNavigationOptions = {
  mode: 'shortcut' | 'project';
  entryPoint?: 'home' | 'my-tasks';
};

type TaskNavigationInfo = Pick<TaskWithSubtasks, 'project_id' | 'parent_task_id'>;

export function useTaskHierarchyNavigation() {
  const navigate = useNavigate();

  return useCallback(
    async (
      target: TaskHierarchyTarget,
      options: TaskHierarchyNavigationOptions,
    ): Promise<boolean> => {
      // Recent Updates and most in-project lists already carry the project id.
      // Do not block the click on a second request just to discover hierarchy
      // metadata; the destination owns the task query and can resolve the
      // parent while it paints its skeleton.
      if (target.projectId && options.mode === 'project') {
        navigate(buildImmediateTaskRoute(target));
        return true;
      }

      const resolved = await resolveTaskHierarchyTarget(target);
      if (!resolved.projectId) return false;

      const routeChain = buildTaskHierarchyRouteChain(resolved, options.mode, options.entryPoint);
      if (routeChain.length === 0) return false;

      if (options.mode === 'shortcut') {
        navigate('/home', { replace: true });
      }

      for (const route of routeChain) {
        navigate(route);
      }

      return true;
    },
    [navigate],
  );
}

async function resolveTaskHierarchyTarget(target: TaskHierarchyTarget): Promise<
  Required<Pick<TaskHierarchyTarget, 'taskId'>> & {
    parentTaskId: string | null;
    projectId?: string;
  }
> {
  if (target.projectId && target.parentTaskId !== undefined) {
    return {
      projectId: target.projectId,
      taskId: target.taskId,
      parentTaskId: target.parentTaskId,
    };
  }

  const taskInfo = await fetchTaskNavigationInfo(target.taskId);

  return {
    projectId: target.projectId ?? taskInfo?.project_id,
    taskId: target.taskId,
    parentTaskId: target.parentTaskId ?? taskInfo?.parent_task_id ?? null,
  };
}

function buildImmediateTaskRoute(target: TaskHierarchyTarget): string {
  const route = `/projects/${segment(target.projectId as string)}/tasks/${segment(target.taskId)}`;
  return target.parentTaskId && target.parentTaskId !== target.taskId
    ? `${route}?parentTaskId=${segment(target.parentTaskId)}`
    : route;
}

async function fetchTaskNavigationInfo(taskId: string): Promise<TaskNavigationInfo | null> {
  try {
    const result = await apiClient<{ task: TaskNavigationInfo }>(`/api/v1/tasks/${taskId}`);
    return result.data.task ?? null;
  } catch {
    return null;
  }
}

function buildTaskHierarchyRouteChain(
  target: { projectId?: string; taskId: string; parentTaskId: string | null },
  mode: TaskHierarchyNavigationOptions['mode'],
  entryPoint: TaskHierarchyNavigationOptions['entryPoint'] = 'home',
): string[] {
  if (!target.projectId) return [];

  const projectRoute = `/projects/${segment(target.projectId)}`;
  const taskRoute = `${projectRoute}/tasks/${segment(target.taskId)}`;
  const chain =
    mode === 'shortcut'
      ? [
          ...(entryPoint === 'my-tasks' ? ['/my-tasks'] : []),
          '/projects',
          `${projectRoute}?tab=tasks`,
        ]
      : ([] as string[]);

  if (target.parentTaskId && target.parentTaskId !== target.taskId) {
    chain.push(`${projectRoute}/tasks/${segment(target.parentTaskId)}`);
  }

  chain.push(taskRoute);
  return chain;
}

function segment(value: string): string {
  return encodeURIComponent(value);
}
